import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Card,
  Row,
  Col,
} from 'react-bootstrap';
import LargeUserPill from '../../common/components/LargeUserPill';
import PeopleLayout from '../layout/PeopleLayout';
import { LoadingAnimation, Icon } from '../../common/components/Utilities';
import ShoutoutsResource from '../../common/resource/ShoutoutsResource';

function ShoutoutsPage(props) {
  return <PeopleLayout {...props} title="Shoutouts Leaderboard" content={<ShoutoutsView {...props} />} />;
}

function groupShoutoutsByWeek(shoutouts) {
  const weekGroups = {};

  shoutouts.forEach((shoutout) => {
    const createdAt = moment(shoutout.created_at);
    const weekStart = createdAt.clone().startOf('isoWeek');
    const weekEnd = createdAt.clone().endOf('isoWeek');
    const groupKey = `${weekStart.format('DD-MM-YYYY')}-${weekEnd.format('DD-MM-YYYY')}`;

    if (!weekGroups[groupKey]) {
      weekGroups[groupKey] = {
        dates: [weekStart.toISOString(), weekEnd.toISOString()],
        shoutouts: [],
        kudos_received: {},
        kudos_given: {},
        most_kudos_received: [],
        most_kudos_given: [],
        weekStartTs: weekStart.valueOf(),
      };
    }

    weekGroups[groupKey].shoutouts.push(shoutout);

    const kudosMap = {
      kudos_received: shoutout.user,
      kudos_given: shoutout.author,
    };

    Object.entries(kudosMap).forEach(([kudosKey, person]) => {
      const userId = person.id;
      if (!weekGroups[groupKey][kudosKey][userId]) {
        weekGroups[groupKey][kudosKey][userId] = { user: person, total: 0 };
      }
      weekGroups[groupKey][kudosKey][userId].total += 1;
    });
  });

  Object.values(weekGroups).forEach((week) => {
    ['kudos_received', 'kudos_given'].forEach((kudosKey) => {
      const sorted = Object.values(week[kudosKey]).sort((a, b) => b.total - a.total);
      const ceiling = sorted.length > 0 ? sorted[0].total : null;
      // eslint-disable-next-line no-param-reassign
      week[`most_${kudosKey}`] = sorted
        .filter((k) => k.total === ceiling)
        .map((k) => k.user);
      // eslint-disable-next-line no-param-reassign
      week[kudosKey] = sorted;
    });

    week.shoutouts.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf());
  });

  return Object.values(weekGroups).sort((a, b) => b.weekStartTs - a.weekStartTs);
}

function ShoutoutsView() {
  const [loading, setLoading] = useState(false);
  const [shoutoutDatas, setShoutoutDatas] = useState([]);

  const loadShoutouts = async () => {
    setLoading(true);
    try {
      const shoutouts = await ShoutoutsResource.index();
      setShoutoutDatas(groupShoutoutsByWeek(shoutouts));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShoutouts();
  }, []);

  if (!shoutoutDatas.length && !loading) {
    return (
      <p>
        No shoutouts just yet. Use the
        {' '}
        <span className="badge badge-primary" style={{ fontSize: '1em' }}>/shoutout</span>
        {' '}
        command in slack to get the party started!
      </p>
    );
  }

  if (!shoutoutDatas.length) {
    return <LoadingAnimation loading={loading} />;
  }

  return (
    <>
      {shoutoutDatas.map((shoutoutData) => (
        <ShoutoutDataRow
          key={shoutoutData.dates[0]}
          shoutoutData={shoutoutData}
        />
      ))}
    </>
  );
}

function ShoutoutDataRow({
  shoutoutData,
}) {
  return (
    <Card className="mt-2">
      <Card.Header>
        <Row>
          <Col>
            From
            {' '}
            <span className="badge badge-dark" style={{ fontSize: '1em' }}>{moment(shoutoutData.dates[0]).format('D MMMM')}</span>
            {' '}
            {moment(shoutoutData.dates[0]).format('YYYY')}
            {' '}
            to
            {' '}
            <span className="badge badge-dark" style={{ fontSize: '1em' }}>{moment(shoutoutData.dates[1]).format('D MMMM')}</span>
            {' '}
            {moment(shoutoutData.dates[1]).format('YYYY')}
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col className="bg-primary text-white p-3 text-center m-3" style={{ marginTop: '0 !important', borderRadius: '5px' }}>
            <h3 className="mb-3">
              <Icon icon="trophy" append="mr-3" />
              Most Kudos Given
            </h3>

            {shoutoutData.most_kudos_given.map((user) => (
              <LargeUserPill user={user} style={{ display: 'inline-block' }} className="m-1" />
            ))}

          </Col>
          <Col className="bg-success text-white p-3 text-center m-3" style={{ marginTop: '0 !important', borderRadius: '5px' }}>
            <h3 className="mb-3">
              <Icon icon="trophy" append="mr-3" />
              Most Kudos Received
            </h3>

            {shoutoutData.most_kudos_received.map((user) => (
              <LargeUserPill user={user} style={{ display: 'inline-block' }} className="m-1" />
            ))}

          </Col>
        </Row>

        <table className="table table-striped mb-0">
          <thead className="thead-dark">
            <tr>
              <th>Shoutout</th>
              <th>Given By</th>
              <th>Received By</th>
              <th>On</th>
            </tr>
          </thead>
          <tbody>

            {shoutoutData.shoutouts.map((shoutout) => (
              <ShoutoutRow key={shoutout.id} shoutout={shoutout} />
            ))}

          </tbody>
        </table>

      </Card.Body>
    </Card>
  );
}

function ShoutoutRow({
  shoutout,
}) {
  return (
    <tr>
      <td style={{ verticalAlign: 'middle' }}>
        {shoutout.content !== null ? shoutout.content : <span className="badge badge-secondary" style={{ fontSize: '1em' }}>Private</span>}
      </td>
      <td style={{ verticalAlign: 'middle' }}>
        <LargeUserPill user={shoutout.author} />
      </td>
      <td style={{ verticalAlign: 'middle' }}>
        <LargeUserPill user={shoutout.user} />
      </td>
      <td style={{ verticalAlign: 'middle' }}>

        {moment(shoutout.created_at).format('D MMMM YYYY')}

      </td>
    </tr>
  );
}

export default ShoutoutsPage;
